import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, styled } from '@mui/material';
import dayjs, { type Dayjs } from 'dayjs';
import DatePicker from '@/components/@IntranetLibrary/DatePicker';
import Label from '@/components/@IntranetLibrary/Label';
import { RadioGroupV2 } from '@/components/@IntranetLibrary/Radio/RadioV2';
import Select from '@/components/@IntranetLibrary/Select';
import RHFTextArea from '@/components/HookForm/RHFTextarea';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import {
  PledgeBox,
  RESIGNATION_PLEDGE_ALERT,
  ResignationEmployeeTable,
  ResignationPledgeText,
  ResignationRoot,
} from '@/components/Approval/@components/Resignation';
import { VEHICLE_COST_HR_MISSING_MESSAGE } from '@/utils/approval/vehicleCost';
import {
  buildResignationSubject,
  RESIGNATION_DETAIL_WRITE_MAX,
  RESIGNATION_OTHER_DETAIL,
  RESIGNATION_PERSONAL_REASON,
  RESIGNATION_REASON_DETAILS,
  RESIGNATION_REASONS,
  type ResignationFormState,
} from '@/utils/approval/resignation';
import { formatAsDate } from '@/utils/format';
import { FieldBlock, FieldHeader } from './styles';
import type { ApprovalFormValues } from './types';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';

const HrMissingNotice = styled(ApprovalNoticeBox)`
  color: var(--dk-danger, #f55060);
`;

const DateRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const SubField = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PledgeAgreeRow = styled(Box)`
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
`;

const PledgeAlert = styled('p')`
  margin: 4px 0 0;
  text-align: right;
  font-size: 12px;
  color: var(--dk-danger, #f55060);
`;

const toPickerValue = (value: string): Dayjs | null =>
  value ? dayjs(value) : null;

const toDateString = (value: Dayjs | null): string =>
  value?.isValid() ? formatAsDate(value) : '';

/** 퇴직원(196) 본문 — 레거시 appr_edit_forms_resignation */
const ResignationField = () => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'resignation' });

  if (!state) {
    return (
      <FieldBlock>
        <HrMissingNotice>{t(VEHICLE_COST_HR_MISSING_MESSAGE)}</HrMissingNotice>
      </FieldBlock>
    );
  }

  const update = (patch: Partial<ResignationFormState>) => {
    const current = getValues('resignation');
    if (!current) return;
    setValue('resignation', { ...current, ...patch }, { shouldDirty: true });
  };

  const handleRetireDate = (value: Dayjs | null) => {
    const retireDate = toDateString(value);
    update({ retireDate });
    // 레거시 — 퇴직일을 고르면 직접 고친 제목도 덮어씀
    if (retireDate) {
      setValue(
        'subject',
        buildResignationSubject(state.employee.name, retireDate),
      );
    }
  };

  return (
    <ResignationRoot>
      <FieldBlock>
        <FieldHeader>
          <InfoSectionTitle>{t('퇴직자 정보')}</InfoSectionTitle>
        </FieldHeader>
        <ResignationEmployeeTable employee={state.employee} />
      </FieldBlock>

      <FieldBlock>
        <FieldHeader>
          <InfoSectionTitle>{t('퇴사 관련 날짜 기입')}</InfoSectionTitle>
        </FieldHeader>
        <DateRow>
          <SubField>
            <Label size={14} isRequired={false} title={t('마지막 근무일')} />
            <DatePicker
              value={toPickerValue(state.lastWorkDate)}
              onChange={(value) =>
                update({ lastWorkDate: toDateString(value) })
              }
              format="YYYY-MM-DD"
              placeholder="yyyy-mm-dd"
              sx={{ width: 180 }}
            />
          </SubField>
          <SubField>
            <Label size={14} isRequired={false} title={t('퇴직일')} />
            <DatePicker
              value={toPickerValue(state.retireDate)}
              onChange={handleRetireDate}
              format="YYYY-MM-DD"
              placeholder="yyyy-mm-dd"
              sx={{ width: 180 }}
            />
          </SubField>
        </DateRow>
      </FieldBlock>

      <FieldBlock>
        <FieldHeader>
          <InfoSectionTitle>{t('퇴직 사유 작성')}</InfoSectionTitle>
        </FieldHeader>
        <SubField>
          <Label size={14} isRequired={false} title={t('퇴직 사유')} />
          <Select
            value={state.reason}
            placeholder={t('퇴직 사유 선택')}
            options={RESIGNATION_REASONS.map((reason) => ({
              value: reason,
              label: t(reason),
            }))}
            onChange={(e) => update({ reason: e.target.value as string })}
            sx={{
              ...approvalOutlineSelectSx,
              alignSelf: 'flex-start',
              minWidth: 150,
            }}
          />
        </SubField>
        {state.reason === RESIGNATION_PERSONAL_REASON && (
          <SubField sx={{ mt: 2.5 }}>
            <Label size={14} isRequired={false} title={t('상세사유')} />
            <RadioGroupV2
              value={state.reasonDetail}
              onChange={(e) => update({ reasonDetail: e.target.value })}
              options={RESIGNATION_REASON_DETAILS.map((detail) => ({
                value: detail,
                label: t(detail),
              }))}
              sx={{ gap: '8px' }}
            />
            {state.reasonDetail === RESIGNATION_OTHER_DETAIL && (
              <RHFTextArea
                name="resignation.reasonDetailWrite"
                placeholder={t('사유 작성')}
                maxLength={RESIGNATION_DETAIL_WRITE_MAX}
                minRows={3}
              />
            )}
          </SubField>
        )}
      </FieldBlock>

      <FieldBlock>
        <FieldHeader>
          <InfoSectionTitle>{t('퇴직 서약서 동의')}</InfoSectionTitle>
        </FieldHeader>
        <PledgeBox>
          <ResignationPledgeText />
        </PledgeBox>
        <PledgeAgreeRow>
          <RadioGroupV2
            row
            value={state.pledgeAgree}
            onChange={(e) =>
              update({
                pledgeAgree: e.target
                  .value as ResignationFormState['pledgeAgree'],
              })
            }
            options={[
              { value: 'Y', label: t('동의함') },
              { value: 'N', label: t('동의하지 않음') },
            ]}
          />
        </PledgeAgreeRow>
        {state.pledgeAgree === 'N' && (
          <PledgeAlert>{t(RESIGNATION_PLEDGE_ALERT)}</PledgeAlert>
        )}
      </FieldBlock>
    </ResignationRoot>
  );
};

export default ResignationField;
